import { useTranslation } from 'react-i18next';
import { Button, Form, Input } from 'antd';
import { useEffect, useRef } from 'react';
import type { InputRef } from 'antd';
import type File from '../../../interfaces/File';
import { Icon } from '../../Core/Icon';

interface Props {
  file?: File;
  isFolder?: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}

export const FileTitleForm = ({ file, isFolder = false, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const inputRef = useRef<InputRef>(null);

  /**
   * Handles the submission of form data, processes and transforms it into a `User` object,
   * and passes the constructed user object to the given submission handler.
   *
   * @param {any} values - The form values submitted by the user. Includes fields such as
   * firstName, lastName, email, role, expirationDate, and privateUser.
   */
  const handleFinish = (values: any) => {
    onSubmit(`${values.title}${file?.fileExtension || ''}`);
  };

  useEffect(() => {
    if (file) {
      // Remove the file extension from the title
      const title = file.fileName.replace(file.fileExtension, '');
      form.setFieldsValue({
        title,
      });
    } else {
      form.resetFields();
    }

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 200);
  }, [file, form]);

  return (
    <Form
      layout="vertical"
      form={form}
      size="large"
      className="pt-6! [&_.ant-form-item]:mb-2! [&_.ant-form-item-explain-error]:hidden! [&_.ant-form-item-label]:hidden!"
      onFinish={handleFinish}
    >
      <Form.Item name="title" rules={[{ required: true }]}>
        <Input
          ref={inputRef}
          autoFocus
          autoCapitalize="none"
          placeholder={t('common.title')}
          maxLength={255}
          addonAfter={
            file?.isFolder || isFolder ? (
              <Icon name="FolderIcon" />
            ) : (
              <span>{file?.fileExtension}</span>
            )
          }
        />
      </Form.Item>
      <div className="mt-4 flex flex-row items-center justify-end gap-2">
        <Button size="middle" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button size="middle" type="primary" htmlType="submit">
          {t('common.save')}
        </Button>
      </div>
    </Form>
  );
};

export default FileTitleForm;
